import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MapPin, Phone, Clock, Mail, MessageCircle, Instagram, Coffee, Croissant, Sandwich, CupSoda } from "lucide-react";
import { api } from "../lib/api";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { MenuList } from "../components/site/MenuList";
import { EnquiryForm } from "../components/site/EnquiryForm";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay: d, ease: "easeOut" },
});

const pillars = [
  { icon: Coffee, label: "Premium Coffee" },
  { icon: Croissant, label: "Fresh Bakes" },
  { icon: Sandwich, label: "Delicious Bites" },
  { icon: CupSoda, label: "Refreshing Drinks" },
];

export default function Home() {
  const [settings, setSettings] = useState({});
  const [items, setItems] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/menu").then((r) => setItems(r.data));
    api.get("/gallery").then((r) => setGallery(r.data));
    api.get("/reviews").then((r) => setReviews(r.data));
    api.get("/offers").then((r) => setOffers(r.data));
  }, []);

  const signature = items.filter((i) => i.signature).slice(0, 4);

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <Navbar />

      {/* HERO */}
      <section id="home" className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={settings.hero_image || ""}
            alt="Espresso at JH Brew Bites"
            className="h-full w-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/90 to-charcoal/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-charcoal/50" />
        </div>

        <div className="relative mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-40">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="text-xs font-semibold uppercase tracking-[0.3em] text-copper"
          >
            {settings.secondary_tagline || "Freshly Brewed. Freshly Baked. Freshly Made."}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 max-w-3xl font-serif text-5xl font-black leading-[0.95] tracking-tight text-cream md:text-6xl lg:text-7xl"
            data-testid="hero-heading"
          >
            JH Brew Bites
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-5 max-w-xl font-serif text-2xl italic text-caramel md:text-3xl"
            data-testid="hero-subheading"
          >
            {settings.tagline || "Brewing Happiness, One Bite at a Time."}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-6 max-w-xl text-sm text-cream/75 md:text-lg"
            data-testid="hero-text"
          >
            {settings.hero_text}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <Link
              to="/menu"
              data-testid="hero-view-menu-btn"
              className="rounded-full bg-copper px-7 py-3.5 text-sm font-semibold text-charcoal transition-colors duration-200 hover:bg-caramel"
            >
              View Menu
            </Link>
            <a
              href="#location"
              data-testid="hero-visit-us-btn"
              className="rounded-full border border-cream/30 px-7 py-3.5 text-sm font-semibold text-cream transition-colors duration-200 hover:border-copper hover:text-caramel"
            >
              Visit Us
            </a>
          </motion.div>

          <div className="mt-16 flex flex-wrap gap-x-8 gap-y-4">
            {pillars.map((p, i) => (
              <motion.div
                key={p.label}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 + i * 0.08 }}
                className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-cream/60"
              >
                <p.icon className="h-4 w-4 text-copper" /> {p.label}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OFFERS */}
      {offers.length > 0 && (
        <section className="border-y border-border bg-coffee/60" data-testid="offers-strip">
          <div className="mx-auto flex max-w-7xl flex-col gap-4 px-5 py-6 md:flex-row md:items-center md:gap-10 md:px-10">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-copper">On Now</span>
            <div className="flex flex-wrap gap-x-10 gap-y-2">
              {offers.map((o) => (
                <p key={o.id} className="text-sm text-cream/80" data-testid={`offer-${o.id}`}>
                  <span className="font-semibold text-cream">{o.title}</span>
                  {o.description ? ` — ${o.description}` : ""}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* STORY */}
      <section id="story" className="mx-auto max-w-7xl px-5 py-20 md:px-10 md:py-32" data-testid="story-section">
        <div className="grid gap-14 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
          <motion.div {...fade()}>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-copper">Our Story</p>
            <h2 className="mt-5 font-serif text-4xl font-bold leading-tight tracking-tight text-cream md:text-5xl">
              {settings.about_title || "Our Story"}
            </h2>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-cream/70 md:text-lg">
              {settings.about_text}
            </p>
            <div className="mt-10 grid max-w-lg grid-cols-3 gap-6 border-t border-border pt-8">
              {[
                ["Small", "Batch Bakes"],
                ["Made", "To Order"],
                ["Cozy", "Corners"],
              ].map(([a, b]) => (
                <div key={a}>
                  <p className="font-serif text-2xl text-caramel">{a}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">{b}</p>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div {...fade(0.15)} className="grid grid-cols-2 gap-4">
            {gallery.slice(0, 4).map((g, i) => (
              <div
                key={g.id}
                className={`overflow-hidden rounded-2xl border border-border ${i % 3 === 0 ? "row-span-2 h-full" : ""}`}
              >
                <img
                  src={g.url}
                  alt={g.caption || "JH Brew Bites"}
                  loading="lazy"
                  className="h-full min-h-[140px] w-full object-cover transition-transform duration-500 ease-out hover:scale-105"
                />
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SIGNATURE */}
      {signature.length > 0 && (
        <section className="border-y border-border bg-card/40 px-5 py-20 md:px-10 md:py-28" data-testid="signature-section">
          <div className="mx-auto max-w-7xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-copper">House Favourites</p>
            <h2 className="mt-4 font-serif text-4xl font-bold tracking-tight text-cream md:text-5xl">Signature Items</h2>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {signature.map((s, i) => (
                <motion.div
                  key={s.id}
                  {...fade(i * 0.08)}
                  data-testid={`signature-item-${s.id}`}
                  className="group overflow-hidden rounded-2xl border border-border bg-card"
                >
                  <div className="h-48 overflow-hidden">
                    <img src={s.image} alt={s.name} loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif text-xl text-cream">{s.name}</h3>
                    <p className="mt-2 text-xs uppercase tracking-[0.15em] text-muted-foreground">{s.category}</p>
                    <p className="mt-3 text-sm font-semibold text-caramel">{s.price ? `₹ ${s.price}` : "₹ Price"}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MENU */}
      <section id="menu" className="mx-auto max-w-7xl px-5 py-20 md:px-10 md:py-32" data-testid="menu-section">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-copper">The Menu</p>
            <h2 className="mt-4 font-serif text-4xl font-bold tracking-tight text-cream md:text-5xl">
              Brewed, Baked & Bitten
            </h2>
          </div>
          <Link to="/menu" data-testid="menu-section-view-all"
            className="rounded-full border border-border px-5 py-2.5 text-sm text-cream/80 transition-colors hover:border-copper hover:text-caramel">
            Open full menu
          </Link>
        </div>
        <div className="mt-12">
          <MenuList items={items} />
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="border-y border-border bg-card/40 px-5 py-20 md:px-10 md:py-28" data-testid="gallery-section">
        <div className="mx-auto max-w-7xl">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-copper">Inside The Café</p>
          <h2 className="mt-4 font-serif text-4xl font-bold tracking-tight text-cream md:text-5xl">Gallery</h2>
          <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
            {gallery.map((g, i) => (
              <motion.div
                key={g.id}
                {...fade(Math.min(i * 0.05, 0.3))}
                data-testid={`gallery-image-${g.id}`}
                className={`overflow-hidden rounded-2xl border border-border ${i % 5 === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
              >
                <img src={g.url} alt={g.caption || "Café moment"} loading="lazy"
                  className="h-full min-h-[150px] w-full object-cover transition-transform duration-500 ease-out hover:scale-105" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section id="reviews" className="mx-auto max-w-7xl px-5 py-20 md:px-10 md:py-32" data-testid="reviews-section">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-copper">Kind Words</p>
        <h2 className="mt-4 font-serif text-4xl font-bold tracking-tight text-cream md:text-5xl">Customer Reviews</h2>
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {reviews.map((r, i) => (
            <motion.blockquote
              key={r.id}
              {...fade(i * 0.07)}
              data-testid={`review-${r.id}`}
              className="rounded-2xl border border-border bg-card p-6"
            >
              <div className="flex gap-1">
                {Array.from({ length: r.rating }).map((_, k) => (
                  <Star key={k} className="h-3.5 w-3.5 fill-caramel text-caramel" />
                ))}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-cream/75">“{r.text}”</p>
              <footer className="mt-5">
                <p className="font-serif text-lg text-cream">{r.name}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{r.location}</p>
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </section>

      {/* LOCATION + CONTACT */}
      <section id="location" className="border-t border-border bg-card/40 px-5 py-20 md:px-10 md:py-28" data-testid="location-section">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-2 lg:gap-20">
          <div id="contact" data-testid="contact-block">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-copper">Visit & Contact</p>
            <h2 className="mt-4 font-serif text-4xl font-bold tracking-tight text-cream md:text-5xl">Come Say Hello</h2>
            <div className="mt-10 space-y-6">
              <div className="flex gap-4">
                <MapPin className="mt-1 h-5 w-5 shrink-0 text-copper" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Address</p>
                  <p className="mt-1 text-base text-cream/85" data-testid="contact-address">{settings.address}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Clock className="mt-1 h-5 w-5 shrink-0 text-copper" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Opening Hours</p>
                  <p className="mt-1 text-base text-cream/85" data-testid="contact-hours">{settings.hours}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Phone className="mt-1 h-5 w-5 shrink-0 text-copper" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Phone</p>
                  <a href={`tel:${settings.phone || ""}`} className="mt-1 block text-base text-cream/85 hover:text-caramel" data-testid="contact-phone">
                    {settings.phone}
                  </a>
                </div>
              </div>
              <div className="flex gap-4">
                <Mail className="mt-1 h-5 w-5 shrink-0 text-copper" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</p>
                  <a href={`mailto:${settings.email || ""}`} className="mt-1 block break-all text-base text-cream/85 hover:text-caramel" data-testid="contact-email">
                    {settings.email}
                  </a>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 pt-2">
                <a href={`https://wa.me/${settings.whatsapp || ""}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn"
                  className="flex items-center gap-2 rounded-full bg-copper px-6 py-3 text-sm font-semibold text-charcoal transition-colors hover:bg-caramel">
                  <MessageCircle className="h-4 w-4" /> WhatsApp Us
                </a>
                <a href={settings.instagram || "#"} target="_blank" rel="noreferrer" data-testid="contact-instagram-btn"
                  className="flex items-center gap-2 rounded-full border border-cream/25 px-6 py-3 text-sm font-semibold text-cream transition-colors hover:border-copper hover:text-caramel">
                  <Instagram className="h-4 w-4" /> Instagram
                </a>
              </div>
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border" data-testid="location-map">
            <iframe
              title="JH Brew Bites location"
              className="h-[340px] w-full lg:h-full"
              src={`https://www.google.com/maps?q=${encodeURIComponent(settings.map_query || settings.address || "cafe")}&output=embed`}
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* ENQUIRY FORM */}
      <section id="enquiry" className="border-t border-border px-5 py-20 md:px-10 md:py-28" data-testid="enquiry-section">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
          <motion.div {...fade()}>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-copper">Enquiries</p>
            <h2 className="mt-4 font-serif text-4xl font-bold leading-tight tracking-tight text-cream md:text-5xl">
              Have a Question? Get in Touch With Us
            </h2>
            <p className="mt-5 max-w-md text-base text-cream/70 md:text-lg">
              We'd love to hear from you. Share your details and our team will get back to you soon.
            </p>
            <p className="mt-8 max-w-md text-sm text-muted-foreground">
              Corporate orders, bulk boxes of nankhatai, catering, birthdays and events — tell us what
              you have in mind and we'll plan it with you.
            </p>
          </motion.div>
          <motion.div {...fade(0.12)}>
            <EnquiryForm />
          </motion.div>
        </div>
      </section>

      <Footer settings={settings} />
    </div>
  );
}
